import React, { useEffect, useCallback, useRef, useState } from 'react';

import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';

import CommentBox from 'components/CommentBox/CommentBox';
import CommentsApi from 'global/commentsApi';
import { Comment } from 'global/types/Comment';

import CommentItem from './CommentItem/CommentItem';
import { ReplyWrapper } from './styles';
import { RepliesState, ReplyListProps } from './types';

const ReplyList = ({
  postId,
  commentId,
  isDeleted,
  onNewReply,
  onDeleteReply,
  modalRef,
  getUserBySlug,
}: ReplyListProps) => {
  const user = useUserStore(userStoreSelector.user);
  const abortRef = useRef<AbortController | null>(null);
  const [replies, setReplies] = useState<RepliesState>({
    status: 'pending',
    data: [],
    error: null,
    total: 0,
  });

  const fetchReplies = useCallback(
    async (take = 5) => {
      setReplies((state) => ({ ...state, status: 'pending' }));
      abortRef.current = new AbortController();

      try {
        const response = await CommentsApi.getReplies({
          commentId: commentId,
          take,
          signal: abortRef.current.signal,
        });

        if (response.error !== null) {
          return setReplies((comments) => ({
            ...comments,
            status: 'rejected',
            error: response.error,
          }));
        }

        setReplies({
          status: 'resolved',
          error: null,
          data: response.comments?.reverse() || [],
          total: response.total,
        });
      } catch (error) {
        if (typeof error === 'object' && error !== null && 'message' in error) {
          const message = (error as Error).message;
          if (message.includes('user aborted a request')) {
            return setReplies((comments) => ({
              ...comments,
              status: 'idle',
            }));
          }

          return setReplies((comments) => ({
            ...comments,
            status: 'rejected',
            error: message,
          }));
        }
      }
    },
    [commentId]
  );

  const deleteReply = (reply: Comment) => {
    setReplies((state) => ({
      ...state,
      total: state.total - 1,
      data: state.data.filter((r) => r.id !== reply.id),
    }));

    onDeleteReply && onDeleteReply();

    if (replies.status === 'pending') {
      abortRef.current?.abort();
      fetchReplies(replies.total - 1);
    }
  };

  const handleNewReply = (newReply: Comment) => {
    setReplies((state) => ({
      ...state,
      total: state.total + 1,
      data: [...state.data, newReply],
    }));

    onNewReply && onNewReply();

    if (replies.status === 'pending') {
      abortRef.current?.abort();
      fetchReplies(replies.total + 1);
    }
  };

  useEffect(() => {
    fetchReplies();
  }, [fetchReplies]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    wrapperRef.current?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [replies]);

  return (
    <ReplyWrapper
      ref={wrapperRef}
      className={replies?.data.length ? 'with-replies' : ''}
    >
      {!!replies?.data.length &&
        replies.data.map((r, i) => (
          <CommentItem
            key={r.id}
            isReply
            comment={r}
            getUserBySlug={getUserBySlug}
            isFirst={i === 0}
            modalRef={modalRef}
            postId={postId}
            onDelete={deleteReply}
          />
        ))}
      {user && (!isDeleted || (isDeleted && !replies.total)) && (
        <CommentBox
          small
          commentId={commentId}
          getUserBySlug={getUserBySlug}
          modalRef={modalRef}
          postId={postId}
          onNewComment={handleNewReply}
        />
      )}
    </ReplyWrapper>
  );
};

export default withEventScope(ReplyList, { module: 'commentReplies' });
